import React, { ChangeEvent, useContext, useEffect } from 'react';
import Header from '#/components/Form/Header/Header';
import { ScrolledContext } from '#/components/Layout/PageWrapper/PageWrapper';
import useProtectedSpotify from '#/hooks/useProtectedAxios';
import {
  createActionArtistAlbumsAddDetails,
  createActionArtistAlbumsReset,
  createActionArtistAlbumsSetIsFollowed,
  createActionArtistAlbumsSetLayout,
  createActionArtistAlbumsSetSelectedAlbumType,
  createActionArtistAlbumsSetTrackIsFollowed,
  createActionArtistAlbumsSetTracks,
  createActionArtistAlbumsStopLoading,
} from '#/redux/actions/artistAlbums';
import { TRootState } from '#/redux/store';
import ApiManager from '#/services/api/ApiManager';
import { IAlbumResponse } from '#/types/API/albums';
import { IArtistAlbums, IArtistResponse } from '#/types/API/artists';
import { spotifyApiConfig } from '#/utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import AlbumBanner from './components/AlbumBanner/AlbumBanner';
import TrackTableHead from '../../../../components/Layout/TracksTableHead/TracksTableHead';
import LoadingPage from '#/components/Layout/LoadingPage/LoadingPage';
import Filters from './components/Filters/Filters';
import { TLayout } from '#/redux/reducers/artistAlbums';
import PreviewLayout from '#/components/Layout/PreviewLayout/PreviewLayout';
import { capitalize } from 'lodash';
import TopItemPreview from '#/components/Layout/TopItemPreview/TopItemPreview';
import { ROUTE_NAMES } from '#/router/routes';
import TrackListItem from '#/components/Layout/TrackListItem/TrackListItem';
import {
  emptyContainsResponse,
  urisParameter,
} from '#/utils/spotifyApiHelpers';

const Discography = () => {
  const scrolled = useContext(ScrolledContext);

  const navigate = useNavigate();

  const { id } = useParams();

  const dispatch = useDispatch();

  const protectedSpotify = useProtectedSpotify();

  const { details, albums, _error, _loading, selectedAlbumType, layout } =
    useSelector((state: TRootState) => state.artistAlbums);

  useEffect(() => {
    const getArtistDetails = async () => {
      if (id) {
        const apiMannager = new ApiManager(spotifyApiConfig, protectedSpotify);

        const responseDetails = await apiMannager.getEntity<IArtistResponse>(
          'artists',
          id
        );

        const responseAlbums = await apiMannager.getEntity<IArtistAlbums>(
          'artists',
          `${id}/albums`,
          {
            include_groups:
              selectedAlbumType === '' ? undefined : selectedAlbumType,
          }
        );

        if ('error' in responseDetails || 'error' in responseAlbums) {
          // TODO: handle error
          //dispatch(createActionArtistErrorDetails());
        } else {
          const details = responseDetails.data;

          const albums = responseAlbums.data.items;

          const albumResponses = await Promise.all(
            albums.map((album) =>
              apiMannager.getEntity<IAlbumResponse>('albums', album.id)
            )
          );

          if (albumResponses.some((response) => 'error' in response)) {
            //TODO: handle error
          } else {
            const albumsDetails = albumResponses.map(
              (response) => (response as { data: IAlbumResponse }).data
            );

            dispatch(
              createActionArtistAlbumsAddDetails({
                details,
                albums: albumsDetails,
              })
            );

            dispatch(createActionArtistAlbumsStopLoading());

            const albumIds = albumsDetails.map((album) => album.id);
            const responseFavAlbum =
              albumIds.length > 0
                ? await apiMannager.getAllEntities<boolean[]>(
                    'libraryContains',
                    {
                      uris: urisParameter('album', albumIds),
                    }
                  )
                : await emptyContainsResponse();

            if ('error' in responseFavAlbum) {
              //TODO: handle error
            } else {
              for (const [
                index,
                isFollowed,
              ] of responseFavAlbum.data.entries()) {
                dispatch(
                  createActionArtistAlbumsSetIsFollowed({
                    album: albumsDetails[index].id,
                    follow: isFollowed,
                  })
                );
              }

              for (const album of albumsDetails) {
                const trackIds = album.tracks.items.map((track) => track.id);
                const responseFavTracks =
                  trackIds.length > 0
                    ? await apiMannager.getAllEntities<boolean[]>(
                        'libraryContains',
                        {
                          uris: urisParameter('track', trackIds),
                        }
                      )
                    : await emptyContainsResponse();
                if ('error' in responseFavTracks) {
                  //TODO: handle error
                } else {
                  const favoritesTracks = responseFavTracks.data;
                  const newTracks = album.tracks.items.map((track, index) => ({
                    ...track,
                    favorite: favoritesTracks[index],
                  }));
                  dispatch(
                    createActionArtistAlbumsSetTracks({
                      album: album.id,
                      tracks: { ...album.tracks, items: newTracks },
                    })
                  );
                }
              }
            }
          }
        }
      }
    };

    getArtistDetails();
  }, [dispatch, id, protectedSpotify, selectedAlbumType]);

  useEffect(() => {
    return () => {
      dispatch(createActionArtistAlbumsReset());
    };
  }, [dispatch]);

  const handleOnChangeSelectedAlbumType = (
    event: ChangeEvent<HTMLSelectElement>
  ) => {
    if (
      event.currentTarget.value === '' ||
      event.currentTarget.value === 'album' ||
      event.currentTarget.value === 'single' ||
      event.currentTarget.value === 'compilation'
    ) {
      dispatch(
        createActionArtistAlbumsSetSelectedAlbumType(event.currentTarget.value)
      );
    }
  };

  const handleOnClickLayout = (layout: TLayout) => {
    dispatch(createActionArtistAlbumsSetLayout(layout));
  };

  return (
    <>
      {_error || _loading ? (
        <LoadingPage />
      ) : (
        <div className="relative">
          <div
            className={`z-10 w-full mt-theme-top-bar-negative-height px-theme-page-padding pt-theme-top-bar-height sticky top-0 bg-theme-darker-gray ${
              scrolled > 0 ? ' shadow-lg shadow-theme-shadow' : ''
            }`}
          >
            <div className=" pt-4 pb-2 flex justify-between items-end">
              <Header
                className=" text-2xl font-bold tracking-tighter"
                type="h1"
              >
                {details?.name}
              </Header>
              <Filters
                onChangeSelectedAlbumType={handleOnChangeSelectedAlbumType}
                onClickLayoutType={handleOnClickLayout}
                albumType={selectedAlbumType ?? ''}
                layout={layout}
              />
            </div>
          </div>
          <div className=" p-theme-page-padding flex flex-col gap-4">
            {layout === 'detailed' ? (
              albums?.map((album) => {
                const handleClickFavorite = async () => {
                  const apiMannager = new ApiManager(
                    spotifyApiConfig,
                    protectedSpotify
                  );
                  if (album.favorite) {
                    const responseRemoveFromFavorites =
                      await apiMannager.deleteEntity('library', '', {
                        uris: urisParameter('album', album.id),
                      });

                    if ('error' in responseRemoveFromFavorites) {
                      //TODO: handle error
                    } else {
                      dispatch(
                        createActionArtistAlbumsSetIsFollowed({
                          album: album.id,
                          follow: false,
                        })
                      );
                    }
                  } else {
                    const responseAddToFavorites =
                      await apiMannager.replaceEntity(
                        'library',
                        '',
                        undefined,
                        {
                          uris: urisParameter('album', album.id),
                        }
                      );

                    if ('error' in responseAddToFavorites) {
                      //TODO: handle error
                    } else {
                      //
                      dispatch(
                        createActionArtistAlbumsSetIsFollowed({
                          album: album.id,
                          follow: true,
                        })
                      );
                    }
                  }
                };

                return (
                  <div key={album.id}>
                    <AlbumBanner
                      uri={album.uri}
                      id={album.id}
                      name={album.name}
                      albumType={album.album_type}
                      releaseYear={Number(album.release_date.slice(0, 4))}
                      image={album.images[0].url}
                      numberOfSongs={album.total_tracks}
                      isFavorite={album.favorite ?? false}
                      onClickFavorite={handleClickFavorite}
                    />
                    <TrackTableHead />
                    {album.tracks.items.map((track, index) => {
                      const handleClickFavoriteTrack = async () => {
                        const apiMannager = new ApiManager(
                          spotifyApiConfig,
                          protectedSpotify
                        );
                        if (track.favorite) {
                          const responseRemoveFromFavorites =
                            await apiMannager.deleteEntity('library', '', {
                              uris: urisParameter('track', track.id),
                            });

                          if ('error' in responseRemoveFromFavorites) {
                            //TODO: handle error
                          } else {
                            dispatch(
                              createActionArtistAlbumsSetTrackIsFollowed({
                                album: album.id,
                                track: track.id,
                                follow: false,
                              })
                            );
                          }
                        } else {
                          const responseAddToFavorites =
                            await apiMannager.replaceEntity(
                              'library',
                              '',
                              undefined,
                              {
                                uris: urisParameter('track', track.id),
                              }
                            );

                          if ('error' in responseAddToFavorites) {
                            //TODO: handle error
                          } else {
                            //
                            dispatch(
                              createActionArtistAlbumsSetTrackIsFollowed({
                                album: album.id,
                                track: track.id,
                                follow: true,
                              })
                            );
                          }
                        }
                      };

                      return (
                        <TrackListItem
                          id={track.id}
                          uri={track.uri}
                          contextUri={album.uri}
                          key={track.id}
                          name={track.name}
                          duration={track.duration_ms}
                          number={index + 1}
                          explicit={track.explicit}
                          followed={track.favorite ?? false}
                          artists={track.artists.map((artist) => ({
                            name: artist.name,
                            id: artist.id,
                          }))}
                          onFavorite={handleClickFavoriteTrack}
                        />
                      );
                    })}
                  </div>
                );
              })
            ) : (
              <PreviewLayout>
                {albums?.map((album) => {
                  const handleOnClick = () =>
                    navigate(`${ROUTE_NAMES.ALBUM}/${album.id}`);

                  return (
                    <TopItemPreview
                      uri={album.uri}
                      key={album.id}
                      image={album.images[0].url}
                      name={album.name}
                      releaseYear={Number(album.release_date.slice(0, 4))}
                      type={capitalize(album.album_type)}
                      onClick={handleOnClick}
                    />
                  );
                })}
              </PreviewLayout>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Discography;
